import { Injectable } from '@angular/core';
import {
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';


@Injectable({ providedIn: 'root' })
export class MSALInstanceService {
  public msalInstance: IPublicClientApplication | null = null;
  private currentAuthType: 'b2c' | 'entraId' | 'guest';

  constructor() {
    const storedAuthType = localStorage.getItem('authType');
    
    if (storedAuthType === 'guest') {
      this.currentAuthType = 'guest';
      // Don't create MSAL instance for guest mode
    } else {
      this.currentAuthType = (storedAuthType as 'b2c' | 'entraId') || 'b2c';
      this.msalInstance = this.createMsalInstance(this.currentAuthType as 'b2c' | 'entraId');
    }
  }
  private createMsalInstance(authType: 'b2c' | 'entraId'): IPublicClientApplication {
    const ssoConfig = (window as any)?.sso;
    
    if (!ssoConfig) {
      console.error('SSO configuration not loaded from /api/config/sso2.js');
      throw new Error('SSO configuration not loaded. Please refresh the page and try again.');
    }

    const authConfig = authType === 'b2c' ? ssoConfig.b2c : ssoConfig.entraId;
    
    if (!authConfig || !authConfig.clientId || !authConfig.authority) {
      console.error(`Invalid SSO configuration for ${authType}:`, authConfig);
      throw new Error(`SSO configuration for ${authType} is incomplete. Please check the server configuration.`);
    }

    const msalConfigs = {
      b2c: {
        auth: {
          clientId: ssoConfig.b2c.clientId,
          authority: ssoConfig.b2c.authority,
          redirectUri: ssoConfig.b2c.redirectUri,
          knownAuthorities: ssoConfig.b2c.knownAuthorities,
          scopes: ssoConfig.b2c.scopes,
        },
        cache: {
          cacheLocation: ssoConfig.cache?.cacheLocation || 'localStorage',
          storeAuthStateInCookie: ssoConfig.cache?.storeAuthStateInCookie || false,
        },
        system: {
          allowPlatformBroker: false
        }
      },
      entraId: {
        auth: {
          clientId: ssoConfig.entraId.clientId,
          authority: ssoConfig.entraId.authority,
          redirectUri: ssoConfig.entraId.redirectUri,
          postLogoutRedirectUri: "/",
          scopes: ssoConfig.entraId.scopes,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
        system: {
          allowPlatformBroker: false
        }
      },
    };
    return new PublicClientApplication(msalConfigs[authType]);

  }
  async switchInstance(authType: 'b2c' | 'entraId' | 'guest'): Promise<void> {
    localStorage.removeItem('authType');
    
    // If switching to guest mode, logout from MSAL and clear instance
    if (authType === 'guest') {
      if (this.msalInstance) {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await this.msalInstance.logoutRedirect();
        }
      }
      this.currentAuthType = 'guest';
      this.msalInstance = null;
      localStorage.setItem('authType', 'guest');
      return;
    }
    
    // Switching to B2C or EntraId
    if (this.msalInstance) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await this.msalInstance.logoutRedirect();
        return;
      }
    }
    
    this.currentAuthType = authType;
    localStorage.setItem('authType', authType);
    this.msalInstance = this.createMsalInstance(authType);
    await this.msalInstance.initialize();
    await this.msalInstance.loginRedirect();
  }

  getInstance(): IPublicClientApplication {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not available in guest mode');
    }
    return this.msalInstance;
  }

  getInstanceOrStub(): IPublicClientApplication {
    if (this.msalInstance) {
      return this.msalInstance;
    }
    return {
      initialize: () => Promise.resolve(),
      acquireTokenSilent: () => Promise.reject('Guest mode'),
      acquireTokenPopup: () => Promise.reject('Guest mode'),
      acquireTokenRedirect: () => Promise.resolve(),
      acquireTokenByCode: () => Promise.reject('Guest mode'),
      loginPopup: () => Promise.reject('Guest mode'),
      loginRedirect: () => Promise.resolve(),
      logout: () => Promise.resolve(),
      logoutRedirect: () => Promise.resolve(),
      logoutPopup: () => Promise.resolve(),
      ssoSilent: () => Promise.reject('Guest mode'),
      getAllAccounts: () => [],
      getAccountByHomeId: () => null,
      getAccountByLocalId: () => null,
      getAccountByUsername: () => null,
      getAccount: () => null,
      setActiveAccount: () => {},
      getActiveAccount: () => null,
      handleRedirectPromise: () => Promise.resolve(null),
      addEventCallback: () => null,
      removeEventCallback: () => {},
      addPerformanceCallback: () => '',
      removePerformanceCallback: () => false,
      enableAccountStorageEvents: () => {},
      disableAccountStorageEvents: () => {},
      getTokenCache: () => ({} as any),
      getLogger: () => ({} as any),
      setLogger: () => {},
      getConfiguration: () => ({} as any),
      clearCache: () => Promise.resolve(),
      hydrateCache: () => Promise.resolve(),
      initializeWrapperLibrary: () => {},
      setNavigationClient: () => {}
    } as IPublicClientApplication;
  }

  setActiveAccount(account: any): void {
    if (!this.msalInstance) {
      return; // No-op in guest mode
    }
    this.msalInstance.setActiveAccount(account);
  }

  logout(): void {
    if (!this.msalInstance) {
      return; // No-op in guest mode
    }
    this.msalInstance.logout();
  }

  getCurrentAuthType(): 'b2c' | 'entraId' | 'guest' {
    return this.currentAuthType;
  }

  getScopes(): string[] {
    if (this.currentAuthType === 'guest') {
      return [];
    }
    return this.currentAuthType === 'b2c' 
      ? (window as any)?.sso?.b2c.scopes 
      : (window as any)?.sso?.entraId.scopes;
  }

  getProtectedResourceMap(): Map<string, string[]> {
    const map = new Map<string, string[]>();
    const scopes = this.getScopes();
    map.set((window as any)?.sso?.api.uri, scopes);
    return map;
  }

}

